import React, { useState } from 'react';
import { Grid, TextField, Typography, Divider, Button, Alert, CircularProgress, Box } from '@mui/material';
import { FormikProps } from 'formik';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

// Define a simplified FormValues interface for this component
interface FormValues {
  registeredAddressLine1: string;
  registeredAddressLine2?: string;
  registeredCity: string;
  registeredCounty?: string;
  registeredPostcode: string;
  companyNumber: string;
  incorporationDate?: string;
  [key: string]: any; // Allow other fields that might be accessed
}

interface Props {
  formik: FormikProps<FormValues>;
  clientId?: string;
  onRefreshSuccess?: (updatedData: any) => void;
}

const RegisteredAddressSection: React.FC<Props> = ({ formik, clientId, onRefreshSuccess }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshSuccess, setRefreshSuccess] = useState<string | null>(null);

  const handleRefreshCompaniesHouse = async () => {
    if (!clientId) {
      setRefreshError('Client ID is required to refresh Companies House data');
      return;
    }

    setRefreshing(true);
    setRefreshError(null);
    setRefreshSuccess(null);

    try {
      const response = await axios.post(`/api/clients/${clientId}/refresh-companies-house`);
      
      if (response.data.success && response.data.client) {
        const client = response.data.client;
        
        // Update formik values with the refreshed data
        formik.setFieldValue('companyName', client.companyName || '');
        formik.setFieldValue('companyNumber', client.companyNumber || '');
        formik.setFieldValue('incorporationDate', client.incorporationDate || '');
        formik.setFieldValue('registeredAddressLine1', client.registeredBusinessAddress?.line1 || '');
        formik.setFieldValue('registeredAddressLine2', client.registeredBusinessAddress?.line2 || '');
        formik.setFieldValue('registeredCity', client.registeredBusinessAddress?.city || '');
        formik.setFieldValue('registeredCounty', client.registeredBusinessAddress?.county || '');
        formik.setFieldValue('registeredPostcode', client.registeredBusinessAddress?.postcode || '');
        
        setRefreshSuccess('Companies House data refreshed successfully');
        
        // Call the callback if provided
        if (onRefreshSuccess) {
          onRefreshSuccess(client);
        }
      } else {
        setRefreshError('Failed to refresh Companies House data');
      }
    } catch (error: any) {
      console.error('Error refreshing Companies House data:', error);
      setRefreshError(
        error.response?.data?.error || 
        'Failed to refresh Companies House data. Please try again.'
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
  <>
    <Grid item xs={12}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Registered Business Address</Typography>
        {clientId && (
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefreshCompaniesHouse}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh from Companies House'}
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        Information from Companies House - Use refresh button to update with latest data
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      {refreshError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefreshError(null)}>
          {refreshError}
        </Alert>
      )}
      
      {refreshSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefreshSuccess(null)}>
          {refreshSuccess}
        </Alert>
      )}
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        id="registeredAddressLine1"
        name="registeredAddressLine1"
        label="Address Line 1"
        value={formik.values.registeredAddressLine1}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        id="registeredAddressLine2"
        name="registeredAddressLine2"
        label="Address Line 2 (Optional)"
        value={formik.values.registeredAddressLine2 || ''}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="registeredCity"
        name="registeredCity"
        label="City/Town"
        value={formik.values.registeredCity}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="registeredCounty"
        name="registeredCounty"
        label="County (Optional)"
        value={formik.values.registeredCounty || ''}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="registeredPostcode"
        name="registeredPostcode"
        label="Postcode"
        value={formik.values.registeredPostcode}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="companyNumber"
        name="companyNumber"
        label="Company Number"
        value={formik.values.companyNumber}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="incorporationDate"
        name="incorporationDate"
        label="Incorporation Date"
        value={formik.values.incorporationDate}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
        }}
      />
    </Grid>
  </>
  );
};

export default RegisteredAddressSection;
